import React from 'react';
import { Col, Row } from 'antd';
import { asText, capitalize, symbol, formatRate, has } from '../configUtils';
import DetailViewV1 from '../../../DetailViewV1';
import ActivityNotesCard from '../../ActivityNotesCard';

/*
 * Job detail config.
 *
 *   tabs / actions / tabContent → the static parts of the page.
 *   header                      → fill-in-the-blanks header description.
 *
 * In `header` you just list which fields go where (title, ids, meta, …). See
 * headerSpec.js for the full list of options. A `field` is the API field name;
 * ['a', 'b'] means "a, or b if a is empty".
 */
export const jobConfig = {
  module: 'job',
  tabs: [
    { key: 'details', label: 'Details' },
    { key: 'candidates', label: 'Candidates' },
  ],
  tabContent: {
    details: {
      render: ({ id, refreshKey, onDescriptionSaved }) =>
        React.createElement(
          Row, { gutter: [16, 16], align: 'stretch' },
          React.createElement(Col, { key: 'main', xs: 24, lg: 16 },
            React.createElement(DetailViewV1, { moduleName: 'job', id, refreshKey, onDescriptionSaved })
          ),
          React.createElement(Col, { key: 'side', xs: 24, lg: 8 },
            React.createElement(ActivityNotesCard, { moduleName: 'job', id })
          )
        ),
    },

  },
  header: {
    title: 'jobTitle',

    // "Client - MSP Id 123"; falls back to the Zinnext job id when neither is set.
    eyebrow: ({ val }) => {
      const parts = [];
      if (has(clientName(val))) parts.push(asText(clientName(val)));
      if (has(mspId(val))) parts.push(`MSP Id ${asText(mspId(val))}`);
      if (parts.length) return parts.join(' - ');
      return has(val('jobReferenceId')) ? String(val('jobReferenceId')) : '';
    },

    clientName: ({ val }) => clientName(val),

       // Shown next to the title.
    titleIds: [
      // { label: 'MS Req ID',     field: ['mspReqId', 'mspreqId', 'msReqId', 'mspId', 'mspRequirementId'] },
      { label: 'Client Req ID', field: ['clientJobReferenceId', 'clientReqId', 'clientRequirementId', 'clientReferenceId'] },
    ],

    status: { field: 'jobStatus' },

    // Shown under the title.
    ids: [
      { label: 'Zinnext Job ID', field: 'jobReferenceId' },
      { label: 'Client Rate',    compute: clientRate },
    ],

    meta: [
      { icon: 'location',   field: 'jobLocation' },
      { icon: 'experience', compute: experienceRange },
      { icon: 'employment', fields: ['employmentType', 'jobRemoteStatus'], join: ' · ' },
    ],

    // Summary numbers on the right.
    stats: [
      { label: 'Target submissions', field: ['targetSubmission', 'targetSubmissions'] },
      { label: 'In Pipeline',        compute: inPipeline, always: true },
    ],

    timestamp: { label: 'Created Date', field: ['createdAt', 'jobCreatedDateTime', 'createdDate'] },
  },
};

// First field (from a list) that has a value — for the little formulas below.
const first = (val, ...keys) => keys.map((k) => val(k)).find(has);

const clientName = (val) => first(val, 'clientName', 'client.name', 'client', 'accountName');
const mspId = (val) => first(val, 'mspReqId', 'mspreqId', 'msReqId', 'mspId', 'mspRequirementId');

// "3 - 6 years" (the upper bound is optional).
function experienceRange({ val }) {
  const from = val('experience.experienceRequiredFrom');
  if (!has(from)) return '';
  const to = val('experience.experienceRequiredTo');
  return `${from}${has(to) ? ` - ${to}` : ''} years`;
}

// Client budget, e.g. "₹34 - 55/Monthly".
function clientRate({ val }) {
  const pay = val('jobPayDetails') ?? {};
  const start = pay.clientBudgetStart ?? first(val, 'clientBudgetStart', 'clientRateStart');
  if (!has(start)) return '';
  return formatRate({
    start,
    end: pay.clientBudgetEnd ?? first(val, 'clientBudgetEnd', 'clientRateEnd'),
    sym: pay.clientBudgetCurrencySymbol ?? symbol(pay.clientBudgetCurrency) ?? '',
    unit: pay.clientBudgetUnit ?? first(val, 'clientBudgetUnit'),
  });
}

// Count of candidates currently in the pipeline (0 when none).
function inPipeline({ val }) {
  const subs = val('submissions');
  const count = first(val, 'pipelineCount', 'PipelineCount', 'inPipeline', 'inPipelineCount')
    ?? (Array.isArray(subs) ? subs.length : undefined);
  return has(count) ? count : 0;
}

export default jobConfig;


export function buildJobHeaderData({ val, has }) {
  let eyebrow;
  const meta = [];

  if (has(val('jobReferenceId'))) eyebrow = String(val('jobReferenceId'));

  const title = [val('jobTitle')].filter(has).map(asText).join(' ') || '-';

  let status = null;
  if (has(val('jobSource'))) {
    status = {
      label: capitalize(val('jobSource')),
      color: '#10b981',
      textColor: '#ffffff',
    };
  }

  if (has(val('jobLocation'))) meta.push({ icon: 'location', text: asText(val('jobLocation')) });

  if (has(val('experience.experienceRequiredFrom'))) {
    const from = val('experience.experienceRequiredFrom');
    const to = val('experience.experienceRequiredTo');
    meta.push({ icon: 'experience', text: `${from}${has(to) ? ` - ${to}` : ''} years` });
  }

  const employment = [val('employmentType'), asText(val('jobRemoteStatus'))]
    .filter(has)
    .join(' - ');
  if (employment) meta.push({ icon: 'employment', text: employment });

  // clientId rides along so callers (e.g. QuickSubmitForm's client-scope
  // gate) can read it off the header data DetailHeaderCard already fetched,
  // instead of issuing a second, duplicate getFormGroups({module:'job'}) call.
  const clientId = has(val('clientId')) ? val('clientId') : null;

  return { eyebrow, title, status, meta, clientId };
}
